// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Patient, Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { ASSESSMENT_CATALOG, type AssessmentCatalogEntry } from '../constants/medical-info';

export interface AssessmentState {
  entry: AssessmentCatalogEntry;
  questionnaire: Questionnaire | undefined;
  latestResponse: QuestionnaireResponse | undefined;
}

export interface UsePatientAssessmentsResult {
  assessments: AssessmentState[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

export function usePatientAssessments(patient: Patient | undefined): UsePatientAssessmentsResult {
  const medplum = useMedplum();
  const [assessments, setAssessments] = useState<AssessmentState[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.all(
        ASSESSMENT_CATALOG.map(async (entry) => {
          const questionnaire = await medplum
            .searchOne('Questionnaire', { name: entry.questionnaireName })
            .catch(() => undefined);
          const latestResponse = questionnaire?.id
            ? await medplum
                .searchOne('QuestionnaireResponse', {
                  subject: `Patient/${patient.id}`,
                  questionnaire: `Questionnaire/${questionnaire.id}`,
                  _sort: '-_lastUpdated',
                })
                .catch(() => undefined)
            : undefined;
          return {
            entry,
            questionnaire: questionnaire as Questionnaire | undefined,
            latestResponse: latestResponse as QuestionnaireResponse | undefined,
          };
        })
      );
      setAssessments(results);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load assessments'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { assessments, loading, error, refresh: fetch };
}
